import { nanoid } from 'nanoid';
import { defineStore } from 'pinia';
import { socket as worker } from '@/common/workers';
import { useCreativeStore } from '@/common/store/creative';
import { useEditorStore } from '@/common/store/editor';
import { SocketRoutes } from '../interfaces';

const broadcastChannel = new BroadcastChannel('WebSocketChannel');

export const useSocketStore = defineStore('socket', {
  state: () => ({
    id: nanoid(),
    webSocketState: WebSocket.CONNECTING,
    token: '',
  }),

  actions: {
    connect(token: string) {
      this.token = token;

      worker.onmessage = (event) => {
        switch (event.data.type) {
          case 'WSState':
            this.webSocketState = event.data.state;
            break;
          default:
            this.handleBroadcast(event.data);
        }
      };

      /* worker.onmessageerror = (e) => {
        console.log(e);
      };

      worker.onerror = (e) => {
        console.log(e);
      }; */

      broadcastChannel.addEventListener('message', (event) => {
        switch (event.data.type) {
          case 'WSState':
            this.webSocketState = event.data.state;
            break;
          default:
            this.handleBroadcast(event.data);
        }
      });

      worker.postMessage({
        action: 'CONNECT',
        token,
      });

      window.addEventListener('beforeunload', () => {
        worker.postMessage({ action: 'CLOSE' });
      });
    },

    handleBroadcast(data: { result: { route: string } }) {
      const { result: { route } } = data;
      switch (route) {
        case SocketRoutes.ENTER:
          this.enterCreative(data.result);
          break;
        case SocketRoutes.LEAVE:
          this.leaveCreative(data.result);
          break;
        case SocketRoutes.EXPORT_CANVAS:
          this.updateExportProgress(data.result);
          break;
        default:
          break;
      }
    },

    enterCreative(result) {
      const creativeStore = useCreativeStore();
      if (result.data?.creativeId !== creativeStore.currentCreative?.id) return;
      creativeStore.users = result.list;
    },

    leaveCreative(result) {
      const creativeStore = useCreativeStore();
      if (result.data?.creativeId !== creativeStore.currentCreative?.id) return;
      creativeStore.users = result.list;
    },

    sendAllTabs(data: object) {
      this.postMessageToWSServer(data);
    },

    sendFocusedTab(data: object) {
      this.postMessageToWSServer({
        ...data,
        from: this.id,
      });
    },

    exportCanvas(data: object) {
      this.postMessageToWSServer({
        ...data,
        token: this.token,
      });
    },

    updateExportProgress(result) {
      const editorStore = useEditorStore();
      const sessionIndex = editorStore.exportSessions
        .findIndex((session) => session.session === result.session);
      // Update export session progress
      if (sessionIndex !== -1) {
        editorStore.exportSessions[sessionIndex] = result;
        return;
      }
      // Add export session to export list
      if (result?.session) {
        editorStore.exportSessions.push(result);
      }
    },

    postMessageToWSServer(input: object) {
      if (this.webSocketState === WebSocket.CONNECTING) {
        console.log('Still connecting to the server, try again later!');
      } else if (
        this.webSocketState === WebSocket.CLOSING
        || this.webSocketState === WebSocket.CLOSED
      ) {
        console.log('Connection Closed!');
      } else {
        // stringify and parse to avoid proxy object errors (ref: C-809)
        worker.postMessage(JSON.parse(JSON.stringify(input)));
      }
    },
  },
});

export default useSocketStore;
